import React from "react";
import SafeIcon from "../../../common/SafeIcon";
import * as FiIcons from "react-icons/fi";
import ClientInvoiceStatusBadge from "../invoices/ClientInvoiceStatusBadge";

const { FiEye, FiFileText } = FiIcons;

function money(value, currency) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(amount);
  }
}

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function ClientBillsTable({
  bills,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onView,
}) {
  const pages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (loading) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-violet-100 border-t-violet-600" />
        <p className="mt-4 text-sm text-slate-600">Loading bills…</p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-left">
          <thead className="bg-slate-50">
            <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <th className="px-5 py-4">Bill #</th>
              <th className="px-5 py-4">Vendor</th>
              <th className="px-5 py-4">Bill Date</th>
              <th className="px-5 py-4">Due Date</th>
              <th className="px-5 py-4 text-right">Total</th>
              <th className="px-5 py-4 text-right">Balance Due</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {bills.map((bill) => (
              <tr
                key={bill.id}
                onClick={() => onView(bill)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") onView(bill);
                }}
                tabIndex={0}
                className="cursor-pointer text-sm outline-none hover:bg-slate-50 focus:bg-violet-50/50"
              >
                <td className="whitespace-nowrap px-5 py-4 font-bold text-slate-900">{bill.bill_number || "—"}</td>
                <td className="px-5 py-4 font-semibold text-slate-800">
                  {bill.vendor?.vendor_name || bill.vendor?.company_name || "Vendor unavailable"}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-slate-600">{dateLabel(bill.bill_date)}</td>
                <td className="whitespace-nowrap px-5 py-4 text-slate-600">{dateLabel(bill.due_date)}</td>
                <td className="whitespace-nowrap px-5 py-4 text-right tabular-nums">{money(bill.total, bill.currency || "USD")}</td>
                <td className="whitespace-nowrap px-5 py-4 text-right font-semibold tabular-nums">{money(bill.balance_due, bill.currency || "USD")}</td>
                <td className="px-5 py-4"><ClientInvoiceStatusBadge status={bill.displayStatus || bill.status} /></td>
                <td className="px-5 py-4 text-right">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onView(bill);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-bold text-violet-700 hover:bg-violet-50"
                  >
                    <SafeIcon icon={FiEye} /> View
                  </button>
                </td>
              </tr>
            ))}
            {bills.length === 0 && (
              <tr>
                <td colSpan="8" className="px-6 py-14 text-center">
                  <SafeIcon icon={FiFileText} className="mx-auto text-3xl text-slate-300" />
                  <p className="mt-3 text-sm font-bold text-slate-800">No bills match these filters</p>
                  <p className="mt-1 text-sm text-slate-500">Try a different search or date range.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <footer className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">
          {totalCount ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount}` : "0 bills"}
        </p>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs disabled:opacity-40">Previous</button>
          <span className="px-2 py-2 text-xs text-slate-500">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => onPageChange(page + 1)} className="rounded-lg border border-slate-200 px-3 py-2 text-xs disabled:opacity-40">Next</button>
        </div>
      </footer>
    </section>
  );
}